import { UserManager, WebStorageStateStore, type User as OidcUser } from 'oidc-client-ts'
import config from '@/config'

const TOKEN_KEY = 'access_token'

export const userManager = new UserManager({
  authority: config.oidcAuthorityUrl,
  client_id: config.oidcClientId,
  redirect_uri: window.location.origin,
  post_logout_redirect_uri: window.location.origin,
  response_type: 'code',
  // offline_access: the refresh token becomes an offline token, immune to
  // Keycloak's SSO Session Idle timeout (the previous scope's refresh token
  // died after ~30min of no activity, forcing a full interactive re-login
  // any time the tab/browser wasn't actively running automaticSilentRenew).
  // Governed instead by the realm's separate, longer-lived Offline Session
  // Max. Requires the Keycloak client to have Offline Access enabled.
  scope: 'openid profile email offline_access',
  automaticSilentRenew: true,
  accessTokenExpiringNotificationTimeInSeconds: 60,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  monitorSession: false,
  loadUserInfo: true,
  revokeTokensOnSignout: true,
})

userManager.events.addUserLoaded((user) => {
  localStorage.setItem(TOKEN_KEY, user.access_token)
})

userManager.events.addUserUnloaded(() => {
  localStorage.removeItem(TOKEN_KEY)
})

userManager.events.addSilentRenewError((error) => {
  // invalid_grant means the refresh token / SSO session is dead; transient
  // network errors are left alone — the 401 retry path recovers later.
  if ((error as { error?: string })?.error === 'invalid_grant') {
    forceRelogin()
  }
})

function currentLocation() {
  return window.location.pathname + window.location.search + window.location.hash
}

/**
 * Bootstraps authentication. Resolves with the logged-in user, or redirects
 * to Keycloak (returning a never-resolving promise while navigation happens).
 */
export async function initAuth(): Promise<OidcUser> {
  const params = new URLSearchParams(window.location.search)

  if (params.has('code') && params.has('state')) {
    try {
      const user = await userManager.signinRedirectCallback()
      const returnTo = (user.state as { returnTo?: string } | undefined)?.returnTo || '/'
      window.history.replaceState({}, document.title, returnTo)
      localStorage.setItem(TOKEN_KEY, user.access_token)
      return user
    } catch (error) {
      // Stale or reused code (e.g. back button) — strip params and fall through.
      console.warn('OIDC callback failed, falling back to stored session:', error)
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  } else if (params.has('error')) {
    console.warn('OIDC error response:', params.get('error'), params.get('error_description'))
    window.history.replaceState({}, document.title, window.location.pathname)
  }

  let user = await userManager.getUser()
  if (user?.expired) {
    user = await renewToken()
  }
  if (user && !user.expired) {
    localStorage.setItem(TOKEN_KEY, user.access_token)
    return user
  }

  await userManager.signinRedirect({ state: { returnTo: currentLocation() } })
  return new Promise<OidcUser>(() => {})
}

let renewInFlight: Promise<OidcUser | null> | null = null

/** Single-flight silent renew — concurrent 401s share one refresh request. */
export function renewToken(): Promise<OidcUser | null> {
  if (!renewInFlight) {
    renewInFlight = userManager
      .signinSilent()
      .catch(() => null)
      .finally(() => {
        renewInFlight = null
      })
  }
  return renewInFlight
}

/** RP-initiated logout: ends the Keycloak SSO session, not just the local one. */
export async function logout() {
  localStorage.removeItem(TOKEN_KEY)
  await userManager.signoutRedirect()
}

/** Drops the local session and restarts the login flow at the current URL. */
export async function forceRelogin() {
  await userManager.removeUser()
  await userManager.signinRedirect({ state: { returnTo: currentLocation() } })
}
